import type { CustomFilterMainCreators } from '@/app/common/filters/surfaces/forms/FilterGroupsComponents.tsx';
import { FILTER_NAME } from '@/app/common/filters/core/filterNames.ts';
import {
  ACCOMMODATION_FACILITY_FILTER_NAMES,
  FACILITY_GROUP_FILTER_NAMES,
} from '@/app/common/filters/groups/facilityGroups.ts';
import { FilterSearchDynamicPropertyName } from '@/app/common/filters/surfaces/forms/custom/FilterSearchDynamicPropertyName.tsx';
import { FilterOvernightFlights } from '@/app/common/filters/surfaces/forms/custom/FilterOvernightFlights.tsx';
import { MealPlanFilterMultiSelect } from '@/app/common/filters/surfaces/forms/custom/MealPlanFilterMultiSelect.tsx';
import { PriceRangeFilter } from '@/app/common/filters/surfaces/forms/custom/PriceRangeFilter.tsx';
import { FilterMultiSelectChips } from '@/app/common/filters/types/multiSelect/FilterMultiSelectChips.tsx';
import { StarsAllFiltersGrid } from '@/app/common/filters/surfaces/forms/custom/StarsAllFiltersGrid.tsx';
import { StarsFilterMultiSelect } from '@/app/common/filters/surfaces/forms/custom/StarsFilterMultiSelect.tsx';
import { MealPlanAllFiltersGrid } from '@/app/common/filters/surfaces/forms/custom/MealPlanAllFiltersGrid.tsx';
import { MealPlanOverlayMultiSelect } from '@/app/common/filters/surfaces/forms/custom/MealPlanOverlayMultiSelect.tsx';
import { BudgetRangeFilter } from '@/app/common/filters/surfaces/forms/custom/BudgetRangeFilter.tsx';

export const customFilterMainCreators: CustomFilterMainCreators = {
  [FILTER_NAME.PROPERTY_NAME]: FilterSearchDynamicPropertyName,
  [FILTER_NAME.OVERNIGHT_FLIGHTS]: FilterOvernightFlights,
  [FILTER_NAME.MEAL_PLAN]: MealPlanFilterMultiSelect,
  [FILTER_NAME.PRICE_RANGE]: PriceRangeFilter,
};

export enum FILTER_SURFACE {
  ALL_FILTERS = 'ALL_FILTERS',
  PILL_OVERLAY = 'PILL_OVERLAY',
}

const CHIPS_MAX_VISIBLE = 7;

// The design shows nine property types before its Show more link.
const PROPERTY_TYPES_MAX_VISIBLE = 9;

export type ChipsPresentation = { maxVisible?: number };

type EvoPresentation = Partial<Record<FILTER_SURFACE, ChipsPresentation>>;

const COLLAPSED_IN_ALL_FILTERS: EvoPresentation = {
  [FILTER_SURFACE.ALL_FILTERS]: { maxVisible: CHIPS_MAX_VISIBLE },
  [FILTER_SURFACE.PILL_OVERLAY]: {},
};

const CHIPS_IN_ALL_FILTERS: EvoPresentation = { [FILTER_SURFACE.ALL_FILTERS]: {} };

const PROPERTY_TYPES_IN_ALL_FILTERS: EvoPresentation = {
  [FILTER_SURFACE.ALL_FILTERS]: { maxVisible: PROPERTY_TYPES_MAX_VISIBLE },
};

const evoPresentation: Readonly<Record<string, EvoPresentation>> = {
  ...Object.fromEntries(
    [...ACCOMMODATION_FACILITY_FILTER_NAMES, ...FACILITY_GROUP_FILTER_NAMES].map((name) => [
      name,
      COLLAPSED_IN_ALL_FILTERS,
    ])
  ),
  [FILTER_NAME.PROPERTY_STYLE]: CHIPS_IN_ALL_FILTERS,
  [FILTER_NAME.PROPERTY_TYPE]: PROPERTY_TYPES_IN_ALL_FILTERS,
};

export const chipsPresentationOf = (
  filterName: string,
  surface: FILTER_SURFACE,
  useEvoFilters: boolean | undefined
): ChipsPresentation | undefined =>
  useEvoFilters && Object.hasOwn(evoPresentation, filterName) ? evoPresentation[filterName][surface] : undefined;

// Per-surface evo renderers that are not chips.
const evoSurfaceCreators: Readonly<Record<FILTER_SURFACE, CustomFilterMainCreators>> = {
  [FILTER_SURFACE.ALL_FILTERS]: {
    [FILTER_NAME.STARS]: StarsAllFiltersGrid,
    [FILTER_NAME.MEAL_PLAN]: MealPlanAllFiltersGrid,
    [FILTER_NAME.PRICE_RANGE]: BudgetRangeFilter,
  },
  [FILTER_SURFACE.PILL_OVERLAY]: {
    [FILTER_NAME.STARS]: StarsFilterMultiSelect,
    [FILTER_NAME.MEAL_PLAN]: MealPlanOverlayMultiSelect,
    [FILTER_NAME.PRICE_RANGE]: BudgetRangeFilter,
  },
};

const withChipCreators = (surface: FILTER_SURFACE): CustomFilterMainCreators => ({
  ...customFilterMainCreators,
  ...Object.fromEntries(
    Object.keys(evoPresentation)
      .filter((filterName) => chipsPresentationOf(filterName, surface, true))
      .map((filterName) => [filterName, FilterMultiSelectChips])
  ),
  ...evoSurfaceCreators[surface],
});

const evoFilterMainCreators: Readonly<Record<FILTER_SURFACE, CustomFilterMainCreators>> = {
  [FILTER_SURFACE.ALL_FILTERS]: withChipCreators(FILTER_SURFACE.ALL_FILTERS),
  [FILTER_SURFACE.PILL_OVERLAY]: withChipCreators(FILTER_SURFACE.PILL_OVERLAY),
};

export const getCustomFilterMainCreators = (
  useEvoFilters: boolean | undefined,
  surface: FILTER_SURFACE
): CustomFilterMainCreators => (useEvoFilters ? evoFilterMainCreators[surface] : customFilterMainCreators);

export const collapsesItsOwnOptions = (
  filterName: string,
  surface: FILTER_SURFACE,
  useEvoFilters: boolean | undefined
): boolean => chipsPresentationOf(filterName, surface, useEvoFilters)?.maxVisible !== undefined;
